import { format } from "date-fns";
import { Locale } from "date-fns";
import {
  DashboardChartMetrics,
  DashboardGraphScope,
  DashboardMetricTotals,
  MetricComparison,
} from "./types";

export const formatInteger = (value: number) =>
  new Intl.NumberFormat("en-US").format(value);

export const formatRevenue = (value: number) =>
  new Intl.NumberFormat("ja-JP", {
    currency: "JPY",
    maximumFractionDigits: 0,
    style: "currency",
  }).format(value);

export const formatCompactRevenue = (value: number) =>
  `¥${Math.round(value / 1000)}k`;

export const formatPercent = (value: number) =>
  `${value > 0 ? "+" : ""}${Math.round(value)}%`;

export const formatMonthLabel = (date: Date, locale: Locale) =>
  format(date, "MMM", { locale });

const formatBucketLabel = (
  date: Date,
  locale: Locale,
  graphScope: DashboardGraphScope,
) => {
  if (graphScope === "week") return format(date, "MMM d", { locale });
  if (graphScope === "year") return format(date, "yyyy", { locale });

  return formatMonthLabel(date, locale);
};

export const addBucketLabels = <T extends DashboardChartMetrics>(
  metrics: T[],
  graphScope: DashboardGraphScope,
  locale: Locale,
): (T & { bucketLabel: string })[] =>
  metrics.map((metric) => ({
    ...metric,
    bucketLabel: formatBucketLabel(metric.bucketStart, locale, graphScope),
  }));

export const getDeltaPercent = (value: number, previousValue: number) => {
  if (previousValue === 0) return value === 0 ? 0 : 100;

  return ((value - previousValue) / previousValue) * 100;
};

export const getComparison = (
  value: number,
  previousValue: number
): MetricComparison => ({
  value,
  previousValue,
  deltaPercent: getDeltaPercent(value, previousValue),
});

export const getAcceptanceRate = ({
  accepted,
  rejected,
}: Pick<DashboardMetricTotals, "accepted" | "rejected">) => {
  const totalAnswered = accepted + rejected;

  if (totalAnswered === 0) return 0;

  return (accepted / totalAnswered) * 100;
};

export const getGuestsPerReservation = ({
  guests,
  reservations,
}: Pick<DashboardMetricTotals, "guests" | "reservations">) =>
  reservations === 0 ? "0.0" : (guests / reservations).toFixed(1);

export const createEmptyTotals = (): DashboardMetricTotals => ({
  accepted: 0,
  cancellationNoShowRevenue: 0,
  guests: 0,
  pending: 0,
  rejected: 0,
  reservations: 0,
  revenue: 0,
  unanswered: 0,
});
